/**
 * THE STATUS CHANNEL, READ — the one module that opens a frame's payload for `head` and `revert`.
 *
 * `stream/socket.ts` is envelope-only by construction (and by a grep). This is where the two control payloads
 * are unpacked, and it is a separate module for a reason worth stating: unpacking them is a JUDGEMENT — which
 * field is the frontier, which is the subject, what the client does about each — and a judgement belongs next
 * to the rules it serves, not inside a transport.
 *
 * ONE PLACE THE SUBJECT/FRONTIER DISTINCTION IS APPLIED. The revert frame carries TWO slots and they mean
 * opposite things:
 *
 *   • the ENVELOPE's `slot` is the CURRENT frontier at build time. It is the LWW clock, it is monotone, and
 *     it is what S5b compares a seed's `asOf.slot` against.
 *   • the PAYLOAD's `slot` is the ABANDONED slot S — the subject. It is LOWER than everything around it.
 *
 * Putting the subject on the envelope was one of the mutations the api design's stitch test must fail on,
 * precisely because it looks harmless: S5b would then compare a forked (low) slot against a seed's (high)
 * slot, never fire, and a forked seed would survive. So this module hands BOTH to every revert consumer and
 * names them differently, and no consumer is ever given a bare slot to guess about.
 */

import type { AsOf, Head, Revert, StatusFrame } from "@kinetiq/wire";
import { asOfStore, headStore } from "../stores/asOf.ts";
import { RevertedSlots } from "./revert.ts";
import { type MarketSocket, marketSocket } from "./socket.ts";

export interface RevertEvent {
  /** The ABANDONED slot, S. */
  readonly subject: Revert;
  /** The frontier the revert was ANNOUNCED at — the envelope's clock. This is what S5b compares against. */
  readonly frontierSlot: number;
}

export type RevertHandler = (e: RevertEvent) => void;
export type HeadHandler = (head: Head, asOf: AsOf) => void;

/**
 * The app-wide status state. One instance, attached to the socket once.
 *
 * It owns `revertedSlots` because that set is a property of the CONNECTION's history, not of any one view: a
 * seed for a view that mounted a second ago must still be filtered against a revert that arrived before it
 * existed.
 */
export class StatusBus {
  readonly revertedSlots = new RevertedSlots();
  private readonly revertHandlers = new Set<RevertHandler>();
  private readonly headHandlers = new Set<HeadHandler>();
  private _asOf: AsOf | null = null;
  /** `true` between a `base`-tier head and the next `processed` one. Every seed taken in that period is
   *  PROVISIONAL: a base answer is the durable base with NO overlay, so it is missing ~12–14 s that nothing
   *  will ever push. O6 re-seeds every open view on the transition back. */
  private lastTier: AsOf["tier"] | null = null;
  readonly counters = { heads: 0, reverts: 0, truncated: 0, tierChanges: 0 };

  attach(socket: MarketSocket): () => void {
    const status = socket.onStatus((f) => this.onFrame(f));
    const state = socket.onStateChange((state) => {
      if (state !== "reconnecting") return;
      this.revertedSlots.clear();
      this._asOf = null;
      this.lastTier = null;
      asOfStore.set(null);
      headStore.set(null);
    });
    return () => {
      status();
      state();
    };
  }

  /** `null` until the first head. NULL IS NOT ZERO: a zero slot renders as "the chain is at genesis" and a
   *  zero second as "56 years behind", and both look like data. Every reader must branch. */
  get asOf(): AsOf | null {
    return this._asOf;
  }

  onHead(h: HeadHandler): () => void {
    this.headHandlers.add(h);
    return () => this.headHandlers.delete(h);
  }

  onRevert(h: RevertHandler): () => void {
    this.revertHandlers.add(h);
    return () => this.revertHandlers.delete(h);
  }

  /** Exposed for the tests and for `status.ts`'s own use; a frame is fed here exactly as the socket
   *  delivered it, WHOLE. */
  onFrame(f: StatusFrame): void {
    if (f.type === "head") {
      this.counters.heads++;
      const head = f.data;
      // THE ONE `asOf` THE APP READS. It is assembled from the ENVELOPE's slot and second — the fold frontier
      // and the chain second at build time — plus the head payload's finality and tier. Building it from the
      // envelope rather than from a payload field means the head frame and every data frame agree about the
      // clock by construction.
      const asOf: AsOf = {
        slot: f.slot,
        second: f.second,
        finalizedSlot: head.finalizedSlot,
        tier: head.tier,
      };
      this._asOf = asOf;
      asOfStore.set(asOf);
      // THE HEALTH HALF, published beside the position. `stalledSec`, `behind` and `shedLevel` are all
      // measurements only the server can make, and a badge that renders the position without them cannot
      // distinguish "the socket is fine and the data is stalled" from "nothing is happening" — which is the
      // exact pair this repo has repeatedly failed to tell apart.
      headStore.set(head);
      // EXACT, not heuristic: a finalized slot can never fork, so it can never be the subject of a future
      // revert, and holding it buys nothing.
      this.revertedSlots.evictBelow(head.finalizedSlot);
      if (this.lastTier !== null && this.lastTier !== head.tier) this.counters.tierChanges++;
      this.lastTier = head.tier;
      for (const h of [...this.headHandlers]) h(head, asOf);
      return;
    }
    if (f.type === "revert") {
      this.counters.reverts++;
      const subject = f.data;
      if (subject.truncated) this.counters.truncated++;
      // R1. Recorded BEFORE the handlers run, so a handler that re-issues a seed cannot race the filter that
      // seed will be checked against.
      this.revertedSlots.add(subject.slot);
      const event: RevertEvent = { subject, frontierSlot: f.slot };
      for (const h of [...this.revertHandlers]) h(event);
      return;
    }
    // `status/error` — a protocol error with no determinable channel. It is NOT rendered to the user (a
    // revert is routine and an error here is almost always a client bug), but it is counted, and the socket
    // has already counted it as a refusal.
  }
}

let bus: StatusBus | null = null;

/** The app-wide bus. */
export function statusBus(): StatusBus {
  if (bus === null) bus = new StatusBus();
  return bus;
}

/**
 * ATTACH THE BUS TO THE SOCKET, EXACTLY ONCE PER BUS.
 *
 * A once-per-process fact, not a per-view one: `revertedSlots` is a property of the CONNECTION's history, and
 * a second attachment would apply every revert TWICE — which evicts the set's oldest entries at double the
 * rate and silently shortens the window in which a forked seed row can still be caught.
 *
 * It lives here rather than in a hook because it has two callers (`useHead`, which the shell mounts for the
 * app's lifetime, and `useStitch`, which every view mounts) and two copies of "attach once" is two registries
 * that each think they are the only one. Attaching also OPENS the socket — `MarketSocket.onStatus` calls
 * `ensureOpen` — so the shell's head badge is what brings the connection up on any route, including the 404.
 */
const attached = new WeakSet<StatusBus>();
export function attachStatusOnce(b: StatusBus = statusBus(), socket?: MarketSocket): void {
  if (attached.has(b)) return;
  attached.add(b);
  b.attach(socket ?? marketSocket());
}

/** Test-only. */
export function __setStatusBus(b: StatusBus | null): void {
  bus = b;
}
